/**
 * AddEditSeriesV2 — full V2 port of the Series edit form. Same
 * state shape, fetch graph, and submit handler as v1
 * (AddEditSeries.js) so the API contract is byte-identical.
 *
 * Sections:
 *   01 Basic info        (title req / description / parent category)
 *   02 Sharing & Media   (series image + deep link image + universal link)
 *   03 Sessions (edit-only) — sortable list with Day input + Dark toggle
 *   04 Danger zone (edit-only)
 *
 * Right rail:
 *   • PreviewCardSE      — centered title cluster preview
 *   • PublishingCardSE   — meta + Activate/Deactivate
 *   • SettingsCardSE     — 3 toggles
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import { apiV2 } from '../../../actions/index';
import { uploadImage, clearMedia } from '../../../actions/mediaActions';
import { fetchAllCategories } from '../../../actions/categoryActions';
import {
  deleteSeries,
  addEditSeries,
} from '../../../actions/seriesActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  Select,
  LinkField,
  UploadTile,
} from '../../redesign/edit';
import PublishingCardSE from './PublishingCardSE';
import SettingsCardSE from './SettingsCardSE';
import PreviewCardSE from './PreviewCardSE';
import SeriesSessionsList from './SeriesSessionsList';

const AddEditSeriesV2 = forwardRef(function AddEditSeriesV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─── State (1:1 with v1) ─────────────────────────────────── */
  const [state, setState] = useState({
    series_title: '',
    series_description: '',
    series_is_active: true,
    series_image: '',
    series_image_preview: '',
    series_image_uploading: false,
    series_text_image_url: '',
    series_text_image_url_preview: '',
    series_text_image_url_uploading: false,
    series_is_dark_mode: false,
    series_is_new: false,
    category: 0,
    sessions: [],
    isSetToEdit: false,
    sessionData: {},
    sortOrder: [],
    _sessions: [],
    universal_link: '',
  });

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const all_categories = useSelector((s) => s.categories?.all_categories);
  const image = useSelector((s) => s.media?.image);

  // Latest-value ref for the page chrome's onLoaded callback —
  // lets the fetch effect stay keyed on `selectedId` alone without
  // re-firing every time the parent re-renders with a new function.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    dispatch(fetchAllCategories());
    dispatch(clearMedia());
    if (isEdit && selectedId) {
      setLoading(true);
      apiV2.get(`/series/${selectedId}`)
        .then((response) => {
          const data = response.data;
          const sessionData = {};
          const sortOrder = [];
          if (Array.isArray(data.sessions)) {
            data.sessions.forEach((s) => {
              sessionData[s.session_id] = {
                series_day: s.series_day || 0,
                session_is_dark_mode: !!s.session_is_dark_mode,
              };
              sortOrder.push(s.session_id.toString());
            });
          }
          setState((prev) => ({
            ...prev,
            series_id: data.series_id,
            series_title: data.series_title || '',
            series_description: data.series_description || '',
            series_is_active: !!data.series_is_active,
            series_image: data.series_image || '',
            series_is_dark_mode: !!data.series_is_dark_mode,
            series_is_new: !!data.series_is_new,
            series_text_image_url: data.series_text_image_url || '',
            universal_link: data.universal_link || '',
            category: data.category_id || 0,
            isSetToEdit: true,
            sessionData,
            sortOrder,
            _sessions: data.sessions || [],
          }));

          // Hand the page chrome just enough to render the sticky
          // header — title + updated_at — so it doesn't have to
          // fire its own parallel GET /series/{id}.
          if (onLoadedRef.current) {
            onLoadedRef.current({
              series_title: data.series_title,
              series_updated_at:
                data.series_updated_at ?? data.updated_at ?? null,
            });
          }
        })
        .catch((error) => {
          console.error('Failed to fetch series:', error);
        })
        .finally(() => setLoading(false));
    }
  }, [dispatch, isEdit, selectedId]);

  /* ─── Image upload settle (1:1 with v1) ────────────────────── */
  useEffect(() => {
    if (!image) return;
    if (state.series_image_uploading) {
      setState((prev) => ({
        ...prev,
        series_image: image,
        series_image_uploading: false,
      }));
      dispatch(clearMedia());
    }
    if (state.series_text_image_url_uploading) {
      setState((prev) => ({
        ...prev,
        series_text_image_url: image,
        series_text_image_url_uploading: false,
      }));
      dispatch(clearMedia());
    }
  }, [image, state.series_image_uploading, state.series_text_image_url_uploading, dispatch]);

  /* ─── Preview-URL cleanup on unmount ───────────────────────── */
  const previewUrlsRef = useRef({});
  previewUrlsRef.current.series_image_preview = state.series_image_preview;
  previewUrlsRef.current.series_text_image_url_preview = state.series_text_image_url_preview;
  useEffect(() => {
    return () => {
      const refs = previewUrlsRef.current;
      if (refs.series_image_preview) window.URL.revokeObjectURL(refs.series_image_preview);
      if (refs.series_text_image_url_preview) window.URL.revokeObjectURL(refs.series_text_image_url_preview);
    };
  }, []);

  /* ─── Handlers ─────────────────────────────────────────────── */
  const setField = (name, value) => setState((prev) => ({ ...prev, [name]: value }));
  const handleFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSessionDataChange = useCallback((id, data) => {
    setState((prev) => ({
      ...prev,
      sessionData: {
        ...prev.sessionData,
        [id]: {
          series_day: parseInt(data.series_day, 10) || 0,
          session_is_dark_mode: !!data.session_is_dark_mode,
        },
      },
    }));
  }, []);

  const handleSessionSort = useCallback(({ oldIndex, newIndex }) => {
    setState((prev) => {
      const next = [...prev.sortOrder];
      const [removed] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, removed);
      return { ...prev, sortOrder: next };
    });
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (state.series_image_preview) {
      window.URL.revokeObjectURL(state.series_image_preview);
    }
    setState((prev) => ({
      ...prev,
      series_image_preview: window.URL.createObjectURL(file),
      series_image_uploading: true,
    }));
    dispatch(uploadImage(file));
  }, [state.series_image_preview, dispatch]);

  const handleDeepLinkImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (state.series_text_image_url_preview) {
      window.URL.revokeObjectURL(state.series_text_image_url_preview);
    }
    setState((prev) => ({
      ...prev,
      series_text_image_url_preview: window.URL.createObjectURL(file),
      series_text_image_url_uploading: true,
    }));
    dispatch(uploadImage(file));
  }, [state.series_text_image_url_preview, dispatch]);

  /* ─── Submit (1:1 with v1) ─────────────────────────────── */
  const submit = useCallback(() => {
    if (!state.series_title?.trim()) {
      Swal.fire({ icon: 'error', text: 'Series name is required', confirmButtonText: 'Ok' });
      return;
    }
    setIsSaving(true);
    const { category, sessions: _ignored, ...series_json } = state;
    void _ignored;

    const formattedSessions = state.sortOrder.map((sessionId) => ({
      [sessionId]: {
        series_day: state.sessionData[sessionId]?.series_day || 0,
        session_is_dark_mode: state.sessionData[sessionId]?.session_is_dark_mode || 0,
      },
    }));

    const options = {
      category_id: category,
      series_json: JSON.stringify(series_json),
      session_ids_json: JSON.stringify(formattedSessions),
    };

    const promise = dispatch(addEditSeries(options, () => navigate('/series')));
    if (promise && typeof promise.finally === 'function') {
      promise.finally(() => setIsSaving(false));
    } else {
      setTimeout(() => setIsSaving(false), 3000);
    }
  }, [state, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete (1:1 with v1) ───────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this series?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Delete Series',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteSeries(state.series_id, () => navigate('/series')));
      }
    });
  };

  /* ─── Derived ─────────────────────────────────────────────── */
  const categoryOptions = useMemo(() => {
    if (!all_categories) return [];
    const list = Array.isArray(all_categories) ? all_categories : Object.values(all_categories);
    return list
      .filter((c) => c && c.category_id)
      .map((c) => ({
        value: String(c.category_id),
        label: c.category_title || 'Untitled',
      }));
  }, [all_categories]);

  const sortableItems = useMemo(() => {
    return (state.sortOrder || []).map((sessionId) => {
      const session = (state._sessions || []).find(
        (s) => s.session_id.toString() === sessionId,
      );
      const data = state.sessionData[sessionId] || {};
      return {
        id: sessionId,
        label: session ? session.session_name : `Session ${sessionId}`,
        seriesDay: data.series_day || 0,
        isDarkMode: !!data.session_is_dark_mode,
      };
    });
  }, [state.sortOrder, state._sessions, state.sessionData]);

  // Distinct day count — best-effort signal for the preview card.
  const distinctDayCount = useMemo(() => {
    const set = new Set();
    Object.values(state.sessionData || {}).forEach((d) => {
      const day = Number(d?.series_day) || 0;
      if (day > 0) set.add(day);
    });
    return set.size;
  }, [state.sessionData]);

  /* ─── Render ─────────────────────────────────────────────── */
  if (!all_categories || loading) {
    return <LoadingBackdrop open />;
  }
  if (isEdit && !state.isSetToEdit) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
        <form
          id="series-form"
          onSubmit={handleSubmit}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          {/* 01 Basic info */}
          <FormSection title="Basic info" num="01">
            <FieldBlock label="Series name" required htmlFor="f-se-title">
              <TextInput
                id="f-se-title"
                name="series_title"
                value={state.series_title}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock label="Description" htmlFor="f-se-desc">
              <Textarea
                id="f-se-desc"
                name="series_description"
                value={state.series_description || ''}
                onChange={handleFieldChange}
                rows={2}
                placeholder="Shown on the series landing page."
              />
            </FieldBlock>
            <FieldBlock
              label="Parent category"
              hint="Series live under a single category — pick the one users will browse from."
              noMargin
            >
              <Select
                value={state.category ? String(state.category) : '0'}
                onChange={(e) =>
                  setField('category', e.target.value === '0' ? 0 : Number(e.target.value))
                }
              >
                <option value="0">— pick a category —</option>
                {categoryOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FieldBlock>
          </FormSection>

          {/* 02 Sharing & Media */}
          <FormSection title="Sharing & Media" num="02">
            <FieldRow cols={2} minColWidth={240}>
              <UploadTile
                label="Series image"
                value={state.series_image_preview || state.series_image}
                accept="image/*"
                uploading={!!state.series_image_uploading}
                onChange={handleImageUpload}
                onRemove={() => {
                  if (state.series_image_preview) window.URL.revokeObjectURL(state.series_image_preview);
                  setState((prev) => ({ ...prev, series_image: '', series_image_preview: '' }));
                }}
              />
              <UploadTile
                label="Deep Link Image"
                hint="Social share card"
                value={state.series_text_image_url_preview || state.series_text_image_url}
                accept="image/*"
                uploading={!!state.series_text_image_url_uploading}
                onChange={handleDeepLinkImageUpload}
                onRemove={() => {
                  if (state.series_text_image_url_preview) window.URL.revokeObjectURL(state.series_text_image_url_preview);
                  setState((prev) => ({ ...prev, series_text_image_url: '', series_text_image_url_preview: '' }));
                }}
              />
            </FieldRow>
            {isEdit && state.universal_link && (
              <div style={{ marginTop: 16 }}>
                <FieldBlock
                  label="Universal Link"
                  hint="Generated automatically · used for share & deep links"
                  noMargin
                >
                  <LinkField value={state.universal_link} />
                </FieldBlock>
              </div>
            )}
          </FormSection>

          {/* 03 Sessions (edit-only) */}
          {isEdit && (
            <FormSection
              title="Sessions"
              num="03"
              right={
                <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                  {sortableItems.length}
                </span>
              }
            >
              <SeriesSessionsList
                items={sortableItems}
                onSortEnd={handleSessionSort}
                onDataChange={handleSessionDataChange}
                emptyText="No sessions in this series yet."
              />
            </FormSection>
          )}

          {/* 04 Danger zone */}
          {isEdit && (
            <FormSection title="Danger zone" num="04">
              <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Button variant="danger" onClick={handleDelete} type="button">
                  Delete series
                </Button>
                <span className="text-text-muted" style={{ fontSize: 12 }}>
                  Permanent — cannot be undone.
                </span>
              </div>
            </FormSection>
          )}
        </form>

        {/* Right rail */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <PreviewCardSE
            state={state}
            sessionCount={sortableItems.length}
            dayCount={distinctDayCount}
          />
          {isEdit && (
            <PublishingCardSE
              state={state}
              sessionCount={sortableItems.length}
              onToggleActive={(next) => setField('series_is_active', !!next)}
            />
          )}
          <SettingsCardSE
            state={state}
            onChange={(name, value) => setField(name, !!value)}
          />
        </aside>
      </div>
    </>
  );
});

export default AddEditSeriesV2;
